import { useTranslation } from 'react-i18next';
import { SHORTCUT_HELP, type CommandId, type ToolId } from '@os/engine';
import { TOOLS } from './Toolbar';
import { Button, Dialog, Kbd } from '../../ui';

const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

export function formatCombo(combo: string, mac = isMac()): string {
  const names: Record<string, string> = mac
    ? { mod: '⌘', shift: '⇧', alt: '⌥', delete: '⌦', backspace: '⌫', pageup: '⇞', pagedown: '⇟' }
    : { mod: 'Ctrl', shift: 'Shift', alt: 'Alt', delete: 'Del', backspace: 'Backspace', pageup: 'PgUp', pagedown: 'PgDn' };
  return combo
    .split('+')
    .map((k) => names[k] ?? k.toUpperCase())
    .join(mac ? '' : '+');
}

const actionKey = (a: ToolId | CommandId) => (TOOLS.includes(a as ToolId) ? `tools.${a}` : `commands.${a}`);

/** The editor's shortcuts as a table (the help dialog, the settings page). */
export function EditorShortcutsTable() {
  const { t } = useTranslation();
  return (
      <table className="w-full font-sans text-sm">
        <thead className="sr-only">
          <tr>
            <th>{t('shortcuts.combo')}</th>
            <th>{t('shortcuts.action')}</th>
          </tr>
        </thead>
        <tbody>
          {SHORTCUT_HELP.map(({ combo, action }) => (
            <tr key={combo} className="border-t border-border">
              <td className="py-1.5 pr-4">
                <Kbd>{formatCombo(combo)}</Kbd>
              </td>
              <td className="py-1.5">{t(actionKey(action))}</td>
            </tr>
          ))}
        </tbody>
      </table>
  );
}

/** Generated from the shared KEYMAP, so the help can never drift from the real shortcuts. */
export function ShortcutsHelp({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog
      title={t('shortcuts.title')}
      onClose={onClose}
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('shortcuts.close')}
        </Button>
      }
    >
      <EditorShortcutsTable />
    </Dialog>
  );
}
